import React from 'react';
import times from 'lodash/times';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useSelector } from 'react-redux';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { WorkflowTile } from './components/WorkflowTile/WorkflowTile';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { TilesGrid } from './ClientStartPage.styles';
import { Shimmer } from '../CommonComponents/OPL/Shimmer/Shimmer';
import { Footer } from './components/Footer/Footer';
import { selectPortalName } from '../CommonSelectors/CommonAreaSelector';
import { isOptedIn } from '../CommonUtils/isOptedIn';
import { useClientStartPage } from './useClientStartPage';

interface Props {
  clientId: string;
}

export const ClientStartPage = ({ clientId }: Props) => {
  const portalName = useSelector(selectPortalName);

  useEffectOnce(() => {
    document.title = `Add Goal | ${portalName}`;
  });

  return (
    <Box as="main" maxWidth={1000} mx="auto" height="100%" display="flex" flexDirection="column">
      <ClientStartPageContent clientId={clientId} />
    </Box>
  );
};

interface ClientStartPageContentProps {
  clientId: string;
}

const ClientStartPageContent = ({ clientId }: ClientStartPageContentProps) => {
  const { isLoading, isError, refetch, data, workflowTilesMap } = useClientStartPage({ clientId });

  const hasClientStartActions = isOptedIn('ClientStartActions');

  if (isLoading) {
    return (
      <>
        <Text as="h1" fontWeight={600} fontSize="h5" mb="8">
          Understand Your Investing Behaviors
        </Text>

        <Text as="p" fontSize="h6" color="neutral.500" mb="32">
          Select a workflow to get started
        </Text>

        <TilesGrid as="section" mb="32">
          {times(3).map(key => (
            <Shimmer key={key} height={172} />
          ))}
        </TilesGrid>

        <Text as="h1" fontWeight={600} fontSize="h5" mb="8">
          Take control of your financial future
        </Text>

        <Text as="p" fontSize="h6" color="neutral.500" mb="32">
          Select a goal to get started
        </Text>

        <TilesGrid as="section">
          {times(Object.keys(workflowTilesMap).length).map(key => (
            <Shimmer key={key} height={172} />
          ))}
        </TilesGrid>

        {hasClientStartActions && <Footer isDisabled clientId={clientId} />}
      </>
    );
  }

  if (isError) {
    return <ErrorState onAction={refetch} my="auto" />;
  }

  if (!data) {
    return null;
  }

  const { financialBehaviourWorkflows, otherWorkflows } = data;

  return (
    <>
      {financialBehaviourWorkflows.length > 0 && (
        <Box mb="32">
          <Text as="h1" fontWeight={600} fontSize="h5" mb="8">
            Understand Your Investing Behaviors
          </Text>

          <Text as="p" fontSize="h6" color="neutral.500" mb="32">
            Select a workflow to get started
          </Text>

          <TilesGrid as="section">
            {financialBehaviourWorkflows.map(({ id, name, markAsCompleted, onClick, ...rest }) => (
              <WorkflowTile
                key={id}
                name={name}
                checked={markAsCompleted}
                onClick={() => {
                  onClick({ isCompleted: markAsCompleted });
                }}
                data-testid={id}
                {...rest}
              />
            ))}
          </TilesGrid>
        </Box>
      )}

      <Text as="h1" fontWeight={600} fontSize="h5" mb="8">
        Take control of your financial future
      </Text>

      <Text as="p" fontSize="h6" color="neutral.500" mb="32">
        Select a goal to get started
      </Text>

      <TilesGrid as="section" data-testid="tiles-grid">
        {otherWorkflows.map(({ id, name, markAsCompleted, onClick, ...rest }) => (
          <WorkflowTile
            key={id}
            name={name}
            checked={markAsCompleted}
            onClick={() => {
              onClick({ isCompleted: markAsCompleted });
            }}
            data-testid={id}
            {...rest}
          />
        ))}
      </TilesGrid>

      {hasClientStartActions && <Footer clientId={clientId} />}
    </>
  );
};
